import {
  IPayerResponse,
  PayeeTaxDocumentSharePermission,
  PayerPayeeStatus
} from "@wingspanhq/payments/dist/interfaces";
import {
  Payer,
  PayerStatus,
  PayerTaxInformationShareStatus
} from "@wingspanhq/wingspan-api-sdk";

/**
 * Adapt a v3 `Payer` into the v1 `IPayerResponse` shape that legacy widgets and
 * prop-drilled components still consume, so they render unchanged while the data
 * source moves to v3. Only display name, company DBA, email, and active/tax-shared
 * status have a v3 source today.
 *
 * The remaining v1 fields are left unset — and per the V3 design (PLA-3198), most
 * are deliberately NOT Payer fields, so consumers must be re-sourced, not wait for
 * the Payer model to grow them:
 * - street address / legal name / individual first+last name: live on
 *   Account/ComplianceEntity behind a graduated informationSharing grant (#1) —
 *   never duplicated onto Payer. (The country is the exception:
 *   `Payer.countryCode` carries it.)
 * - QuickBooks integration: `Payer.quickbooksCustomerId` IS a v3 Payer field and
 *   `PayerUpdate` writes it — it is left unmapped here only because the v1
 *   `integration.quickbooks` shape also carries an `itemId`, which the
 *   single-payer read schema omits (the server sends it; `PayerRow` and
 *   `PayerEngagement` both declare it). Consumers read the two ids from those
 *   sources directly rather than through this adapter (#6).
 * - payerSettings / AutoPay: intentionally cut from the payee Payer surface (#5).
 * - payment-method / bank data: genuine design hole (#4).
 * Consumers reading these degrade to empty; see PLA-3198 for the correct source.
 */
export const selectorPayerResponseFromV3 = (payer: Payer): IPayerResponse => {
  const status =
    payer.status === PayerStatus.ACTIVATED
      ? PayerPayeeStatus.Active
      : PayerPayeeStatus.Inactive;
  const shareTaxDocument =
    payer.taxInformationShare?.status === PayerTaxInformationShareStatus.ACTIVE
      ? PayeeTaxDocumentSharePermission.Allow
      : undefined;
  const doingBusinessAs = payer.profile?.company?.doingBusinessAs;

  // `wsName` resolves `pOwnedName` from `payeeOwnedData.payerName`, the last name
  // it tries before falling back to the raw email, and it is the info panel's
  // "Name" row. `profile.displayName` is the v3 identity of a counterparty (see
  // [[selectorCounterpartyDisplayName]]) and was never mapped across.
  //
  // Measured on integration, `displayName` is populated on 0 of 25 clients —
  // only `profile.company.doingBusinessAs` is universal — so in practice this
  // maps nothing today and the "Name" row stays absent. It is mapped anyway
  // because it is the correct source the moment the field is populated; it must
  // NOT be conditioned on differing from the DBA, which only turned a present
  // name into a missing row.
  const payerName = payer.profile?.displayName?.trim() || undefined;

  return {
    payerId: payer.id,
    user: { email: payer.email },
    member: {
      profile: {
        company: { doingBusinessAs },
        // Country only — it's a real Payer field (`countryCode`). Street/city/
        // state stay on Account/ComplianceEntity behind the informationSharing
        // grant, so "Location" resolves to the country alone rather than blank.
        address: { country: payer.countryCode }
      }
    },
    payeeOwnedData: {
      status,
      shareTaxDocument,
      payerCompanyName: doingBusinessAs,
      payerName,
      // Invoice CC recipients — now a first-class v3 Payer field
      // (`notificationCcEmails`, WIN-15229), so this no longer degrades to empty.
      payerEmailCC: payer.notificationCcEmails,
      // `wsName` resolves the client's external id from here (it reads
      // `payerOwnedData.payeeExternalId || payeeOwnedData.payerExternalId`).
      // `Payer.externalId` has always been a v3 field — it just wasn't mapped
      // across, so the "External ID" row rendered empty.
      payerExternalId: payer.externalId ?? undefined
    },
    payerOwnedData: { status }
  } as unknown as IPayerResponse;
};
